// src/components/modules/TAI/TAI.js

import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Typography,
  Card,
  Row,
  Col,
  Spin,
  message,
  Button,
  Space,
  Statistic,
  Badge,
} from "antd";
import {
  ReloadOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CarOutlined,
  NodeIndexOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import TAIFilters from "./TAIFilters";
import TAITrasladosList from "./TAITrasladosList";
import TAIAsignacionMasiva from "./TAIAsignacionMasiva";
import TAIExportacion from "./TAIExportacion";
import TrasladoDialog from "../CenterControl/TrasladoDialog";
import MenuContextualTAI from "./MenuContextualTAI";
import "./TAI.css";

const { Content } = Layout;
const { Title } = Typography;

const TAI = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [traslados, setTraslados] = useState([]);
  const [estadisticas, setEstadisticas] = useState({});
  const [total, setTotal] = useState(0);
  const [selectedTraslados, setSelectedTraslados] = useState([]);
  const [asignacionModalOpen, setAsignacionModalOpen] = useState(false);
  const [exportacionModalOpen, setExportacionModalOpen] = useState(false);
  const [programacionModalOpen, setProgramacionModalOpen] = useState(false);
  const [transportadoras, setTransportadoras] = useState([]);

  // Estados para el menú contextual
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedTrasladoForMenu, setSelectedTrasladoForMenu] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    fechaInicio: dayjs().format("YYYY-MM-DD"),
    fechaFin: dayjs().add(7, "day").format("YYYY-MM-DD"),
    transportadora_id: null,
    estado_asignacion: null,
    trayecto_tipo: null,
    zona: null,
    empresa: null,
    status: [],
    searchQuery: "",
  });

  // Función para verificar permisos
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      if (user.es_administrador && user.sinRol) return true;

      return user.modulosPermisos?.some(
        (mp) => mp.modulo?.toLowerCase() === "tai" && mp.permiso === permission
      );
    },
    [user]
  );

  // Manejadores del menú contextual
  const handleContextMenu = useCallback(
    (event, traslado) => {
      event.preventDefault();
      setSelectedTrasladoForMenu(traslado);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
          : null
      );
    },
    [contextMenu]
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Cargar traslados TAI
  const fetchTraslados = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (filters.fechaInicio) params.append("fechaInicio", filters.fechaInicio);
      if (filters.fechaFin) params.append("fechaFin", filters.fechaFin);
      if (filters.transportadora_id) params.append("transportadora_id", filters.transportadora_id);
      if (filters.estado_asignacion) params.append("estado_asignacion", filters.estado_asignacion);
      if (filters.trayecto_tipo) params.append("trayecto_tipo", filters.trayecto_tipo);
      if (filters.zona) params.append("zona", filters.zona);
      if (filters.empresa) params.append("empresa", filters.empresa);
      if (filters.searchQuery) params.append("searchQuery", filters.searchQuery);
      if (filters.status && filters.status.length > 0) {
        filters.status.forEach((s) => params.append("status[]", s));
      }

      const response = await api.get(`/tai?${params.toString()}`);

      if (response.data.success) {
        setTraslados(response.data.data);
        setTotal(response.data.total);
        setEstadisticas(response.data.estadisticas || {});
      }
    } catch (error) {
      console.error("Error al cargar traslados TAI:", error);
      message.error("Error al cargar traslados TAI");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTraslados();
  }, [fetchTraslados]);

  // Cargar transportadoras al montar
  useEffect(() => {
    const fetchTransportadoras = async () => {
      try {
        const response = await api.get("/tai/transportadoras-activas");
        if (response.data.success) {
          setTransportadoras(response.data.data);
        }
      } catch (error) {
        console.error("Error al cargar transportadoras:", error);
      }
    };
    fetchTransportadoras();
  }, []);

  // Manejar cambio de filtros
  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setSelectedTraslados([]);
  };

  // Manejar selección de traslados
  const handleSelectTraslado = (trasladoId, selected) => {
    setSelectedTraslados((prev) => {
      if (selected) {
        return [...prev, trasladoId];
      } else {
        return prev.filter((id) => id !== trasladoId);
      }
    });
  };

  // Seleccionar todos
  const handleSelectAll = (selected) => {
    if (selected) {
      // Solo seleccionar traslados pendientes de asignación
      const pendientes = traslados
        .filter((t) => t.tai_estado_asignacion === "pendiente")
        .map((t) => t.id);
      setSelectedTraslados(pendientes);
    } else {
      setSelectedTraslados([]);
    }
  };

  // Abrir modal de asignación masiva
  const handleOpenAsignacion = () => {
    if (selectedTraslados.length === 0) {
      message.warning("Seleccione al menos un traslado para asignar");
      return;
    }
    setAsignacionModalOpen(true);
  };

  // Callback después de asignación exitosa
  const handleAsignacionSuccess = () => {
    setAsignacionModalOpen(false);
    setSelectedTraslados([]);
    fetchTraslados();
    message.success("Asignación realizada correctamente");
  };

  // Calcular estadísticas para badges
  const pendientes = estadisticas.pendiente || 0;
  const asignados = estadisticas.asignado || 0;
  const confirmados = estadisticas.confirmado || 0;
  const ejecutados = estadisticas.ejecutado || 0;
  const cancelados = estadisticas.cancelado || 0;

  return (
    <Layout className="tai-layout">
      <Content className="tai-content">
        {/* Header */}
        <div className="tai-header">
          <div className="tai-header-title">
            <NodeIndexOutlined className="tai-header-icon" />
            <Title level={3} style={{ margin: 0 }}>
              Coordinación TAI
            </Title>
            <Badge count={total} showZero style={{ backgroundColor: "#722ed1" }} />
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTraslados}
              loading={loading}
            >
              Actualizar
            </Button>
            {hasPermission("PROGRAMAR_TAI") && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setProgramacionModalOpen(true)}
                style={{ backgroundColor: "#13c2c2" }}
              >
                Programar
              </Button>
            )}
            {hasPermission("EXPORTAR_TAI") && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => setExportacionModalOpen(true)}
                style={{ color: "#52c41a", borderColor: "#52c41a" }}
              >
                Exportar
              </Button>
            )}
            {hasPermission("ASIGNAR_TAI") && selectedTraslados.length > 0 && (
              <Button
                type="primary"
                icon={<TeamOutlined />}
                onClick={handleOpenAsignacion}
                style={{ backgroundColor: "#722ed1" }}
              >
                Asignar Transportadora ({selectedTraslados.length})
              </Button>
            )}
          </Space>
        </div>

        {/* Estadísticas */}
        <Row gutter={[16, 16]} className="tai-stats">
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="tai-stat-card stat-pendiente">
              <Statistic
                title="Pendientes"
                value={pendientes}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="tai-stat-card stat-asignado">
              <Statistic
                title="Asignados"
                value={asignados}
                prefix={<TeamOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="tai-stat-card stat-confirmado">
              <Statistic
                title="Confirmados"
                value={confirmados}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="tai-stat-card stat-ejecutado">
              <Statistic
                title="Ejecutados"
                value={ejecutados}
                prefix={<CarOutlined />}
                valueStyle={{ color: "#13c2c2" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="tai-stat-card stat-cancelado">
              <Statistic
                title="Cancelados"
                value={cancelados}
                prefix={<CloseCircleOutlined />}
                valueStyle={{ color: "#ff4d4f" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <TAIFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          loading={loading}
        />

        {/* Lista de traslados */}
        <Spin spinning={loading}>
          <TAITrasladosList
            traslados={traslados}
            selectedTraslados={selectedTraslados}
            onSelectTraslado={handleSelectTraslado}
            onSelectAll={handleSelectAll}
            onRefresh={fetchTraslados}
            hasPermission={hasPermission}
            onContextMenu={handleContextMenu}
          />
        </Spin>

        {/* Modal de asignación masiva */}
        <TAIAsignacionMasiva
          open={asignacionModalOpen}
          onClose={() => setAsignacionModalOpen(false)}
          trasladoIds={selectedTraslados}
          onSuccess={handleAsignacionSuccess}
        />

        {/* Modal de exportación */}
        <TAIExportacion
          open={exportacionModalOpen}
          onClose={() => setExportacionModalOpen(false)}
          transportadoras={transportadoras}
        />

        {/* Modal de programación TAI */}
        {programacionModalOpen && (
          <TrasladoDialog
            open={programacionModalOpen}
            onClose={() => setProgramacionModalOpen(false)}
            onSubmit={() => {
              setProgramacionModalOpen(false);
              // Refrescar lista después de programar
              setTimeout(() => fetchTraslados(), 1000);
            }}
            isTai={true} // Solo mostrar autorizaciones TAI
          />
        )}

        {/* Menú contextual */}
        <MenuContextualTAI
          traslado={selectedTrasladoForMenu}
          contextMenu={contextMenu}
          onClose={handleCloseContextMenu}
          onRefresh={fetchTraslados}
          hasPermission={hasPermission}
        />
      </Content>
    </Layout>
  );
};

export default TAI;
